import React, { useState } from 'react';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import type { ImageListType } from 'react-images-uploading';
import styles from '../furnishingContainer.module.scss';
import { ImageUploader } from './ImageUploader';
import { SectionHeader } from './SectionHeader';
// @ts-ignore - CKEditor doesn't have types
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore - CKEditor doesn't have types
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import type {
  FurnishingPackage,
  FurnishingModel,
  ShowRoom,
  ItemSpec,
  PackageModel,
  ModelPrice,
  AddOn,
  AddOnItem
} from '../types';
import {
  deleteFurnishingPackage,
  saveItemSpec,
  updateItemSpec,
  updateItemSpecImage,
  deleteItemSpec,
  saveModel,
  updateModel,
  savePrices,
  updatePrice,
  deleteOffer,
  saveShowRoom,
  updateShowRoom,
  deleteShowRoom,
  updateFurnishingPackage,
  deleteModel,
  updateModelImage,
  updatePackageImage,
  updateShowRoomImages,
  saveAddOns,
  updateAddOn,
  deleteAddOn,
  AddOnResponse
} from '../furnishing.service';
import type { ErrorInfo } from '../../shared/interfaces/error-info.interface';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import { store } from 'react-notifications-component';
import {
  notificationBase,
  notificationDismiss
} from '../../shared/services/helpers/notification-helper/notification.helper';
import { PUBLIC_PATH } from '../../shared/config/routes-config';
import { useFurnishingUnitTypes } from '../FurnishingUnitTypesContext';

type NotificationType = 'success' | 'danger' | 'warning';

const notify = (id: string, message: string, type: NotificationType, title?: string): void => {
  store.addNotification({
    ...notificationBase,
    ...notificationDismiss,
    id,
    title: title ?? (type === 'success' ? 'Success' : type === 'danger' ? 'Error' : 'Warning'),
    message,
    type
  });
};

interface FurnishingPackageCardProps {
  pkg: FurnishingPackage;
  displayLanguage: 'en' | 'ar';
  onPackageUpdate: (updatedPkg: FurnishingPackage) => void;
  onPackageRemove: (packageId: string) => void;
  onConfirmDialog: (title: string, message: string, onConfirm: () => void) => void;
}

const generateId = () => `state_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
const isTemporaryId = (id: string) => String(id)?.startsWith('state_');

const emptyItemSpec: Omit<ItemSpec, 'id'> = {
  name: '',
  nameAr: '',
  description: '',
  descriptionAr: '',
  image: ''
};

const emptyShowRoom: Omit<ShowRoom, 'id'> = {
  name: '',
  nameAr: '',
  images: [],
  itemSpecs: []
};

const emptyModel: Omit<FurnishingModel, 'id'> = {
  name: '',
  nameAr: '',
  color: '#F5A623',
  image: '',
  showRooms: [],
  prices: []
};

const emptyAddOnItem: AddOnItem = {
  name: '',
  nameAr: '',
  price: 0
};

export const FurnishingPackageCard: React.FC<FurnishingPackageCardProps> = ({
  pkg,
  displayLanguage,
  onPackageUpdate,
  onPackageRemove,
  onConfirmDialog
}) => {
  const { unitTypeOptions, loadingUnitTypes } = useFurnishingUnitTypes();

  // UI State
  const [isEditingPackage, setIsEditingPackage] = useState(false);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  const [expandedShowRooms, setExpandedShowRooms] = useState<Set<string>>(new Set());
  const [expandedItemSpecs, setExpandedItemSpecs] = useState<Set<string>>(new Set());

  // Image State
  const [packageImages, setPackageImages] = useState<{ dataURL?: string; file?: File }[]>([]);
  const [modelImages, setModelImages] = useState<
    Record<string, { dataURL?: string; file?: File }[]>
  >({});
  const [showRoomImages, setShowRoomImages] = useState<
    Record<string, { dataURL?: string; file?: File }[]>
  >({});
  const [itemSpecImages, setItemSpecImages] = useState<
    Record<string, { dataURL?: string; file?: File }[]>
  >({});

  // Saving State
  const [savingPackage, setSavingPackage] = useState(false);
  const [savingModels, setSavingModels] = useState<Set<string>>(new Set());
  const [savingShowRooms, setSavingShowRooms] = useState<Set<string>>(new Set());
  const [savingShowRoomImages, setSavingShowRoomImages] = useState<Set<string>>(new Set());
  const [savingItemSpecs, setSavingItemSpecs] = useState<Set<string>>(new Set());
  const [savingPrices, setSavingPrices] = useState<Set<string>>(new Set());
  const [savingAddOns, setSavingAddOns] = useState<Set<string>>(new Set());
  const [collapsedPrices, setCollapsedPrices] = useState<Set<string>>(new Set());

  // AddOns State
  const [expandedAddOns, setExpandedAddOns] = useState<Set<string>>(new Set());
  const [newAddOnGroup, setNewAddOnGroup] = useState<{
    unitTypeId: string;
    addons: AddOnItem[];
  } | null>(null);

  const addOnNameOptions = [
    { value: 'Accessories', label: 'Accessories', labelAr: 'الملحقات' },
    { value: 'Beddings', label: 'Beddings', labelAr: 'الفراش' },
    { value: 'Essentials', label: 'Essentials', labelAr: 'أساسيات' },
    { value: 'Appliances', label: 'Appliances', labelAr: 'الأجهزة' },
    { value: 'Kitchen', label: 'Kitchen', labelAr: 'المطبخ' }
  ];

  // Toggle handlers
  const handleToggleEditPackage = (): void => {
    setIsEditingPackage((prev) => !prev);
  };

  const handleToggleModel = (modelId: string): void => {
    setExpandedModels((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });
  };

  const handleToggleShowRoom = (showRoomId: string): void => {
    setExpandedShowRooms((prev) => {
      const n = new Set(prev);
      if (n.has(showRoomId)) n.delete(showRoomId);
      else n.add(showRoomId);
      return n;
    });
  };

  const handleToggleItemSpec = (itemSpecId: string): void => {
    setExpandedItemSpecs((prev) => {
      const n = new Set(prev);
      if (n.has(itemSpecId)) n.delete(itemSpecId);
      else n.add(itemSpecId);
      return n;
    });
  };

  // Package handlers
  const handlePackageChange = (
    field: 'name' | 'nameAr' | 'descriptionEn' | 'descriptionAr' | 'order',
    value: string
  ): void => {
    onPackageUpdate({ ...pkg, [field]: value });
  };

  const handlePackageImageChange = async (imageList: ImageListType): Promise<void> => {
    setPackageImages(imageList);
    if (imageList[0]?.dataURL) {
      onPackageUpdate({ ...pkg, image: imageList[0].dataURL });
    }

    if (pkg.id && !isTemporaryId(pkg.id) && imageList[0]?.file) {
      try {
        const response = await updatePackageImage(pkg.id, imageList[0].file);
        if (!isErrorInfo(response)) {
          notify('update-package-image-success', 'Package image updated successfully', 'success');
          if (response.image) {
            onPackageUpdate({ ...pkg, image: response.image });
          }
        } else {
          notify('update-package-image-failed', 'Failed to update package image', 'danger');
        }
      } catch {
        notify(
          'update-package-image-error',
          'An error occurred while updating the package image',
          'danger'
        );
      }
    }
  };

  const handleSavePackage = async (): Promise<void> => {
    if (!pkg.name || !pkg.nameAr || !pkg.descriptionEn || !pkg.descriptionAr || !pkg.order) {
      notify(
        'package-validation-failed',
        'Please fill in all required fields (name, nameAr, description, descriptionAr, order)',
        'warning',
        'Validation Error'
      );
      return;
    }

    if (!pkg.id) return;

    setSavingPackage(true);

    try {
      const formData: Record<string, string | File> = {
        name: pkg.name,
        nameAr: pkg.nameAr,
        description: pkg.descriptionEn,
        descriptionAr: pkg.descriptionAr,
        order: String(pkg.order || '')
      };

      const response = await updateFurnishingPackage(pkg.id, formData);

      if (!isErrorInfo(response)) {
        notify('save-package-success', 'Package updated successfully', 'success');

        onPackageUpdate({
          ...pkg,
          name: response.name || pkg.name,
          nameAr: response.nameAr || pkg.nameAr,
          descriptionEn: response.description || pkg.descriptionEn,
          descriptionAr: response.descriptionAr || pkg.descriptionAr,
          image: response.image || pkg.image,
          order: response.order !== undefined ? response.order : pkg.order
        });
      } else {
        notify('save-package-failed', 'Failed to update package', 'danger');
      }
    } catch {
      notify('save-package-error', 'An error occurred while updating the package', 'danger');
    } finally {
      setSavingPackage(false);
    }
  };

  const handleSavePackageAndClose = async () => {
    await handleSavePackage();
    setIsEditingPackage(false);
  };

  const handleRemovePackage = (): void => {
    const packageId = pkg.id;
    if (!packageId) return;

    onConfirmDialog(
      'Delete Package',
      'Are you sure you want to delete this package? This action cannot be undone.',
      () => {
        deleteFurnishingPackage(packageId).then((response: PackageModel | ErrorInfo) => {
          if (!isErrorInfo(response)) {
            notify('delete-package-success', 'Package deleted successfully', 'success', '');
            onPackageRemove(packageId);
          } else {
            notify('delete-package-failed', 'Failed to delete package', 'danger', '');
          }
        });
      }
    );
  };

  // Model handlers
  const handleAddModel = (): void => {
    const newModel: FurnishingModel = { id: generateId(), ...emptyModel };
    onPackageUpdate({ ...pkg, models: [...(pkg.models || []), newModel] });
  };

  const handleRemoveModel = (modelId: string): void => {
    onConfirmDialog(
      'Delete Style',
      'Are you sure you want to delete this Style? All associated show rooms and items will be deleted.',
      () => {
        if (modelId) {
          deleteModel(modelId).then((response: unknown) => {
            if (!isErrorInfo(response as ErrorInfo)) {
              notify('delete-style-success', 'Style deleted successfully', 'success', '');
              onPackageUpdate({
                ...pkg,
                models: (pkg.models || []).filter((m) => m.id !== modelId)
              });
            } else {
              notify('delete-style-failed', 'Failed to delete Style', 'danger', '');
            }
          });
        }
      }
    );
  };

  const handleModelChange = (
    modelId: string,
    field: keyof Omit<FurnishingModel, 'id' | 'showRooms'>,
    value: string
  ): void => {
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) => (m.id === modelId ? { ...m, [field]: value } : m))
    });
  };

  const handleModelImageChange = async (
    modelId: string,
    imageList: ImageListType
  ): Promise<void> => {
    setModelImages((prev) => ({ ...prev, [modelId]: imageList }));
    if (imageList[0]?.dataURL) {
      handleModelChange(modelId, 'image', imageList[0].dataURL);
    }

    if (!isTemporaryId(modelId) && imageList[0]?.file) {
      try {
        const response = await updateModelImage(modelId, imageList[0].file);
        if (!isErrorInfo(response)) {
          notify('update-style-image-success', 'Style image updated successfully', 'success');
          if (response.image) {
            handleModelChange(modelId, 'image', response.image);
          }
        } else {
          notify('update-style-image-failed', 'Failed to update style image', 'danger');
        }
      } catch {
        notify(
          'update-style-image-error',
          'An error occurred while updating the style image',
          'danger'
        );
      }
    }
  };

  const handleSaveModel = async (model: FurnishingModel): Promise<void> => {
    if (!model.name || !model.nameAr || !model.color || !model.order) {
      notify(
        'type-validation-failed',
        'Please fill in all required fields (name, nameAr, color, order)',
        'warning',
        'Validation Error'
      );
      return;
    }

    if (!pkg.id) {
      notify('type-save-failed', 'Package ID is missing', 'danger');
      return;
    }

    setSavingModels((prev) => new Set(prev).add(model.id));

    try {
      const isNewModel = isTemporaryId(model.id);
      let response: FurnishingModel | ErrorInfo;

      if (isNewModel) {
        response = await saveModel({
          name: model.name,
          nameAr: model.nameAr,
          color: model.color,
          asset: modelImages[model.id]?.[0]?.file,
          packageId: pkg.id,
          order: Number(model.order)
        });
      } else {
        response = await updateModel(model.id, {
          name: model.name,
          nameAr: model.nameAr,
          color: model.color,
          asset: modelImages[model.id]?.[0]?.file,
          order: Number(model.order)
        });
      }

      if (!isErrorInfo(response)) {
        notify(
          'save-style-success',
          isNewModel ? 'Style created successfully' : 'Style updated successfully',
          'success'
        );

        if (response.id && isNewModel) {
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === model.id ? { ...m, id: response.id } : m
            )
          });
        }
      } else {
        notify(
          'save-style-failed',
          isNewModel ? 'Failed to create style' : 'Failed to update style',
          'danger'
        );
      }
    } catch {
      notify('save-style-error', 'An error occurred while saving the style', 'danger');
    } finally {
      setSavingModels((prev) => {
        const next = new Set(prev);
        next.delete(model.id);
        return next;
      });
    }
  };

  // Price handlers
  const updatePriceField = (
    modelId: string,
    priceIndex: number,
    updates: Partial<ModelPrice>
  ): void => {
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? {
              ...m,
              prices: (m.prices || []).map((p, idx) =>
                idx === priceIndex ? { ...p, ...updates } : p
              )
            }
          : m
      )
    });
  };

  const handleAddPrice = (modelId: string): void => {
    const newPrice: ModelPrice = { unitTypeGroupId: '', price: 0 };
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId ? { ...m, prices: [...(m.prices || []), newPrice] } : m
      )
    });
  };

  const handleRemovePrice = async (modelId: string, priceIndex: number): Promise<void> => {
    const model = pkg.models?.find((m) => m.id === modelId);
    const price = model?.prices?.[priceIndex];

    if (price?.id) {
      const response = await deleteOffer(price.id);
      if (isErrorInfo(response)) {
        notify('delete-price-failed', 'Failed to delete price', 'danger');
        return;
      }
      notify('delete-price-success', 'Price deleted successfully', 'success');
    }

    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? { ...m, prices: (m.prices || []).filter((_, idx) => idx !== priceIndex) }
          : m
      )
    });
  };

  const handlePriceChange = (
    modelId: string,
    priceIndex: number,
    field: 'unitTypeGroupId' | 'price',
    value: string | number
  ): void => {
    updatePriceField(modelId, priceIndex, { [field]: value } as Partial<ModelPrice>);
  };

  const handleSavePrices = async (modelId: string, prices: ModelPrice[]): Promise<void> => {
    if (!prices || prices.length === 0) {
      notify('prices-validation-failed', 'No prices to save', 'warning', 'Validation Error');
      return;
    }

    if (isTemporaryId(modelId)) {
      notify(
        'prices-style-not-saved',
        'Please save the style first before saving prices',
        'warning'
      );
      return;
    }

    if (!pkg.id) {
      notify('prices-save-failed', 'Package ID is missing', 'danger');
      return;
    }

    const packageId = pkg.id;
    setSavingPrices((prev) => new Set(prev).add(modelId));

    try {
      const newPrices = prices.filter((p) => !p.id);
      const existingPrices = prices.filter((p) => p.id);
      let hasError = false;

      if (newPrices.length > 0) {
        const pricesPayload = newPrices.map((p) => ({
          packageId,
          modelId,
          unitTypeGroupId: String(p.unitTypeGroupId || ''),
          price: p.price
        }));
        const createResponse = await savePrices(pricesPayload);
        if (isErrorInfo(createResponse)) {
          hasError = true;
          notify('save-prices-failed', 'Failed to create new prices', 'danger');
        }
      }

      for (const price of existingPrices) {
        const updateResponse = await updatePrice(price.id ?? '', {
          packageId,
          modelId,
          unitTypeGroupId: String(price.unitTypeGroupId || ''),
          price: price.price
        });
        if (isErrorInfo(updateResponse)) {
          hasError = true;
          notify(
            `update-price-failed-${price.id}`,
            `Failed to update price for unit type ${price.unitTypeGroupId}`,
            'danger'
          );
        }
      }

      if (!hasError) {
        notify('save-prices-success', 'Prices saved successfully', 'success', 'Success');
      }
    } catch {
      notify('save-prices-error', 'An error occurred while saving prices', 'danger');
    } finally {
      setSavingPrices((prev) => {
        const next = new Set(prev);
        next.delete(modelId);
        return next;
      });
    }
  };

  // ShowRoom handlers
  const handleAddShowRoom = (modelId: string): void => {
    const newShowRoom: ShowRoom = { id: generateId(), ...emptyShowRoom };
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId ? { ...m, showRooms: [...m.showRooms, newShowRoom] } : m
      )
    });
  };

  const handleRemoveShowRoom = (modelId: string, showRoomId: string): void => {
    onConfirmDialog(
      'Delete Show Room',
      'Are you sure you want to delete this show room? All associated item specifications will be deleted.',
      () => {
        if (showRoomId && !isTemporaryId(showRoomId)) {
          deleteShowRoom(showRoomId).then((response: unknown) => {
            if (!isErrorInfo(response as ErrorInfo)) {
              notify('delete-showroom-success', 'Show room deleted successfully', 'success', '');
              onPackageUpdate({
                ...pkg,
                models: (pkg.models || []).map((m) =>
                  m.id === modelId
                    ? { ...m, showRooms: m.showRooms.filter((sr) => sr.id !== showRoomId) }
                    : m
                )
              });
            } else {
              notify('delete-showroom-failed', 'Failed to delete show room', 'danger', '');
            }
          });
        } else {
          // For temporary (unsaved) show rooms, just remove from local state
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === modelId
                ? { ...m, showRooms: m.showRooms.filter((sr) => sr.id !== showRoomId) }
                : m
            )
          });
        }
      }
    );
  };

  const handleShowRoomChange = (
    modelId: string,
    showRoomId: string,
    key: keyof Omit<ShowRoom, 'id' | 'images' | 'itemSpecs'>,
    value: string | number
  ): void => {
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? {
              ...m,
              showRooms: m.showRooms.map((sr) =>
                sr.id === showRoomId ? { ...sr, [key]: value } : sr
              )
            }
          : m
      )
    });
  };

  const handleShowRoomImageChange = (
    modelId: string,
    showRoomId: string,
    imageList: ImageListType
  ): void => {
    setShowRoomImages((prev) => ({ ...prev, [showRoomId]: imageList }));
    const imageUrls = imageList.map((img) => img.dataURL).filter((u): u is string => !!u);
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? {
              ...m,
              showRooms: m.showRooms.map((sr) =>
                sr.id === showRoomId ? { ...sr, images: imageUrls } : sr
              )
            }
          : m
      )
    });
  };

  const handleUpdateShowRoomImages = async (modelId: string, showRoomId: string): Promise<void> => {
    const imageList = showRoomImages[showRoomId] || [];
    const newFiles = imageList.filter((img) => img.file).map((img) => img.file as File);
    const existingImages = imageList
      .filter((img) => !img.file && img.dataURL && !img.dataURL.startsWith('data:'))
      .map((img) => img.dataURL as string);

    if (isTemporaryId(showRoomId)) {
      notify(
        'showroom-images-not-saved',
        'Please save the show room first before updating images',
        'warning'
      );
      return;
    }

    if (newFiles.length === 0 && existingImages.length === 0) {
      notify('showroom-images-empty', 'No images to update', 'warning');
      return;
    }

    setSavingShowRoomImages((prev) => new Set(prev).add(showRoomId));

    try {
      const response = await updateShowRoomImages(showRoomId, newFiles, existingImages);
      if (!isErrorInfo(response)) {
        notify(
          'update-showroom-images-success',
          'Show room images updated successfully',
          'success'
        );
        if (response.images) {
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === modelId
                ? {
                    ...m,
                    showRooms: m.showRooms.map((sr) =>
                      sr.id === showRoomId ? { ...sr, images: response.images } : sr
                    )
                  }
                : m
            )
          });
        }
      } else {
        notify('update-showroom-images-failed', 'Failed to update show room images', 'danger');
      }
    } catch {
      notify(
        'update-showroom-images-error',
        'An error occurred while updating show room images',
        'danger'
      );
    } finally {
      setSavingShowRoomImages((prev) => {
        const next = new Set(prev);
        next.delete(showRoomId);
        return next;
      });
    }
  };

  const handleSaveShowRoom = async (modelId: string, showRoom: ShowRoom): Promise<void> => {
    if (!showRoom.name || !showRoom.nameAr || !showRoom.order) {
      notify(
        'showroom-validation-failed',
        'Please fill in both English and Arabic room names and order',
        'warning',
        'Validation Error'
      );
      return;
    }

    const isNewShowRoom = isTemporaryId(showRoom.id);
    setSavingShowRooms((prev) => new Set(prev).add(showRoom.id));

    try {
      let response: { id: string; name?: string; nameAr?: string; images?: string[] } | ErrorInfo;

      if (isNewShowRoom) {
        response = await saveShowRoom({
          modelId: modelId,
          name: showRoom.name,
          nameAr: showRoom.nameAr,
          order: showRoom.order,
          images:
            showRoomImages[showRoom.id]?.map((img) => img.file).filter((f): f is File => !!f) || []
        });
      } else {
        response = await updateShowRoom(showRoom.id, {
          name: showRoom.name,
          nameAr: showRoom.nameAr,
          order: Number(showRoom.order)
        });
      }

      if (!isErrorInfo(response)) {
        notify(
          'save-showroom-success',
          isNewShowRoom ? 'Show room created successfully' : 'Show room updated successfully',
          'success'
        );
        if (response.id && isNewShowRoom) {
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === modelId
                ? {
                    ...m,
                    showRooms: m.showRooms.map((sr) =>
                      sr.id === showRoom.id
                        ? {
                            ...sr,
                            id: response.id,
                            ...(response?.images && { images: response.images })
                          }
                        : sr
                    )
                  }
                : m
            )
          });
        }
      } else {
        notify('save-showroom-failed', 'Failed to save show room', 'danger');
      }
    } catch {
      notify('save-showroom-error', 'An error occurred while saving the show room', 'danger');
    } finally {
      setSavingShowRooms((prev) => {
        const next = new Set(prev);
        next.delete(showRoom.id);
        return next;
      });
    }
  };

  // ItemSpec handlers
  const handleAddItemSpec = (modelId: string, showRoomId: string): void => {
    const newItemSpec: ItemSpec = { id: generateId(), ...emptyItemSpec };
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? {
              ...m,
              showRooms: m.showRooms.map((sr) =>
                sr.id === showRoomId ? { ...sr, itemSpecs: [...sr.itemSpecs, newItemSpec] } : sr
              )
            }
          : m
      )
    });
  };

  const handleRemoveItemSpec = (modelId: string, showRoomId: string, itemSpecId: string): void => {
    onConfirmDialog(
      'Delete Item Specification',
      'Are you sure you want to delete this item specification?',
      () => {
        if (itemSpecId && !isTemporaryId(itemSpecId)) {
          deleteItemSpec(itemSpecId).then((response: unknown) => {
            if (!isErrorInfo(response as ErrorInfo)) {
              notify(
                'delete-itemspec-success',
                'Item specification deleted successfully',
                'success',
                ''
              );
              onPackageUpdate({
                ...pkg,
                models: (pkg.models || []).map((m) =>
                  m.id === modelId
                    ? {
                        ...m,
                        showRooms: m.showRooms.map((sr) =>
                          sr.id === showRoomId
                            ? {
                                ...sr,
                                itemSpecs: sr.itemSpecs.filter((is) => is.id !== itemSpecId)
                              }
                            : sr
                        )
                      }
                    : m
                )
              });
            } else {
              notify('delete-itemspec-failed', 'Failed to delete item specification', 'danger', '');
            }
          });
        } else {
          // For temporary (unsaved) item specs, just remove from local state
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === modelId
                ? {
                    ...m,
                    showRooms: m.showRooms.map((sr) =>
                      sr.id === showRoomId
                        ? { ...sr, itemSpecs: sr.itemSpecs.filter((is) => is.id !== itemSpecId) }
                        : sr
                    )
                  }
                : m
            )
          });
        }
      }
    );
  };

  const handleItemSpecChange = (
    modelId: string,
    showRoomId: string,
    itemSpecId: string,
    key: keyof Omit<ItemSpec, 'id'>,
    value: string | number
  ): void => {
    onPackageUpdate({
      ...pkg,
      models: (pkg.models || []).map((m) =>
        m.id === modelId
          ? {
              ...m,
              showRooms: m.showRooms.map((sr) =>
                sr.id === showRoomId
                  ? {
                      ...sr,
                      itemSpecs: sr.itemSpecs.map((is) =>
                        is.id === itemSpecId ? { ...is, [key]: value } : is
                      )
                    }
                  : sr
              )
            }
          : m
      )
    });
  };

  const handleItemSpecImageChange = async (
    modelId: string,
    showRoomId: string,
    itemSpecId: string,
    imageList: ImageListType
  ): Promise<void> => {
    setItemSpecImages((prev) => ({ ...prev, [itemSpecId]: imageList }));
    if (imageList[0]?.dataURL) {
      handleItemSpecChange(modelId, showRoomId, itemSpecId, 'image', imageList[0].dataURL);
    }

    if (!isTemporaryId(itemSpecId) && imageList[0]?.file) {
      try {
        const response = await updateItemSpecImage(itemSpecId, imageList[0].file);
        if (!isErrorInfo(response)) {
          notify(
            'update-itemspec-image-success',
            'Item specification image updated successfully',
            'success'
          );
          if (response.image) {
            handleItemSpecChange(modelId, showRoomId, itemSpecId, 'image', response.image);
          }
        } else {
          notify(
            'update-itemspec-image-failed',
            'Failed to update item specification image',
            'danger'
          );
        }
      } catch {
        notify(
          'update-itemspec-image-error',
          'An error occurred while updating the item specification image',
          'danger'
        );
      }
    }
  };

  const handleSaveItemSpec = async (
    modelId: string,
    showRoomId: string,
    itemSpec: ItemSpec
  ): Promise<void> => {
    if (!itemSpec.name || !itemSpec.nameAr || !itemSpec.order) {
      notify(
        'itemspec-validation-failed',
        'Please fill in both English and Arabic item names and order',
        'warning',
        'Validation Error'
      );
      return;
    }

    if (!pkg.id) {
      notify('itemspec-save-failed', 'Package ID is missing', 'danger');
      return;
    }

    const isNewItemSpec = isTemporaryId(itemSpec.id);
    setSavingItemSpecs((prev) => new Set(prev).add(itemSpec.id));

    try {
      let response: { id: string } | ErrorInfo;

      if (isNewItemSpec) {
        response = await saveItemSpec({
          name: itemSpec.name,
          nameAr: itemSpec.nameAr,
          description: itemSpec.description,
          descriptionAr: itemSpec.descriptionAr,
          image: itemSpecImages[itemSpec.id]?.[0]?.file,
          showRoomId: showRoomId,
          order: itemSpec.order
        });
      } else {
        response = await updateItemSpec(itemSpec.id, {
          name: itemSpec.name,
          nameAr: itemSpec.nameAr,
          description: itemSpec.description,
          descriptionAr: itemSpec.descriptionAr,
          image: itemSpec.image,
          order: Number(itemSpec.order)
        });
      }

      if (!isErrorInfo(response)) {
        notify(
          'save-itemspec-success',
          isNewItemSpec
            ? 'Item specification created successfully'
            : 'Item specification updated successfully',
          'success'
        );
        if (response.id && isNewItemSpec) {
          onPackageUpdate({
            ...pkg,
            models: (pkg.models || []).map((m) =>
              m.id === modelId
                ? {
                    ...m,
                    showRooms: m.showRooms.map((sr) =>
                      sr.id === showRoomId
                        ? {
                            ...sr,
                            itemSpecs: sr.itemSpecs.map((is) =>
                              is.id === itemSpec.id ? { ...is, id: response.id } : is
                            )
                          }
                        : sr
                    )
                  }
                : m
            )
          });
        }
      } else {
        notify('save-itemspec-failed', 'Failed to save item specification', 'danger');
      }
    } catch {
      notify(
        'save-itemspec-error',
        'An error occurred while saving the item specification',
        'danger'
      );
    } finally {
      setSavingItemSpecs((prev) => {
        const next = new Set(prev);
        next.delete(itemSpec.id);
        return next;
      });
    }
  };

  // AddOns handlers
  const handleToggleAddOnGroup = (groupId: string): void => {
    setExpandedAddOns((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleTogglePrices = (modelId: string): void => {
    setCollapsedPrices((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) next.delete(modelId);
      else next.add(modelId);
      return next;
    });
  };

  const handleStartNewAddOnGroup = (): void => {
    setNewAddOnGroup({
      unitTypeId: '',
      addons: [{ ...emptyAddOnItem }]
    });
  };

  const handleCancelNewAddOnGroup = (): void => {
    setNewAddOnGroup(null);
  };

  const handleNewAddOnGroupUnitTypeChange = (unitTypeId: string): void => {
    if (newAddOnGroup) {
      setNewAddOnGroup({ ...newAddOnGroup, unitTypeId });
    }
  };

  const handleAddNewAddOnItem = (): void => {
    if (newAddOnGroup) {
      setNewAddOnGroup({
        ...newAddOnGroup,
        addons: [...newAddOnGroup.addons, { ...emptyAddOnItem }]
      });
    }
  };

  const handleRemoveNewAddOnItem = (index: number): void => {
    if (newAddOnGroup) {
      setNewAddOnGroup({
        ...newAddOnGroup,
        addons: newAddOnGroup.addons.filter((_, i) => i !== index)
      });
    }
  };

  const handleNewAddOnItemChange = (
    index: number,
    field: 'name' | 'nameAr' | 'price' | 'order',
    value: string | number
  ): void => {
    if (newAddOnGroup) {
      const updatedAddons = [...newAddOnGroup.addons];
      updatedAddons[index] = { ...updatedAddons[index], [field]: value };
      setNewAddOnGroup({ ...newAddOnGroup, addons: updatedAddons });
    }
  };

  const handleSaveNewAddOnGroup = async (): Promise<void> => {
    if (!newAddOnGroup || !pkg.id) {
      notify('addons-save-failed', 'Package ID is missing', 'danger');
      return;
    }

    if (!newAddOnGroup.unitTypeId) {
      notify(
        'addons-validation-failed',
        'Please select a unit type',
        'warning',
        'Validation Error'
      );
      return;
    }

    if (newAddOnGroup.addons.length === 0) {
      notify(
        'addons-validation-failed',
        'Please add at least one add-on item',
        'warning',
        'Validation Error'
      );
      return;
    }

    const invalidAddOns = newAddOnGroup.addons.some(
      (addon) => !addon.name || !addon.nameAr || addon.price <= 0 || !addon.order
    );
    if (invalidAddOns) {
      notify(
        'addons-validation-failed',
        'Please fill in all add-on fields (name, nameAr, price)',
        'warning',
        'Validation Error'
      );
      return;
    }

    setSavingAddOns((prev) => new Set(prev).add('new'));

    try {
      const response = await saveAddOns({
        packageId: pkg.id,
        unitTypeId: newAddOnGroup.unitTypeId,
        addons: newAddOnGroup.addons
      });

      if (!isErrorInfo(response)) {
        notify('save-addons-success', 'Add-ons created successfully', 'success');
        // Response is an array of created addons
        const newAddons = Array.isArray(response) ? response : [response];
        onPackageUpdate({
          ...pkg,
          addOns: [...(pkg.addOns || []), ...newAddons]
        });
        setNewAddOnGroup(null);
      } else {
        notify('save-addons-failed', 'Failed to create add-ons', 'danger');
      }
    } catch {
      notify('save-addons-error', 'An error occurred while creating add-ons', 'danger');
    } finally {
      setSavingAddOns((prev) => {
        const next = new Set(prev);
        next.delete('new');
        return next;
      });
    }
  };

  const handleUpdateAddOnItem = async (addonItem: AddOn): Promise<void> => {
    const addonId = String(addonItem.id || addonItem._id || '');

    if (!addonId) {
      notify('addons-update-failed', 'Add-on item ID is missing', 'danger');
      return;
    }

    if (!addonItem.name || !addonItem.nameAr || addonItem.price <= 0 || !addonItem.order) {
      notify(
        'addons-validation-failed',
        'Please fill in all add-on fields (name, nameAr, price, order)',
        'warning',
        'Validation Error'
      );
      return;
    }

    setSavingAddOns((prev) => new Set(prev).add(addonId));

    try {
      let response: AddOnResponse | ErrorInfo;

      if (isTemporaryId(addonId)) {
        response = await saveAddOns({
          packageId: pkg.id || '',
          unitTypeId: addonItem.unitTypeId || '',
          addons: [
            {
              name: addonItem.name,
              nameAr: addonItem.nameAr,
              price: addonItem.price,
              order: addonItem.order
            }
          ]
        });
      } else {
        response = await updateAddOn(addonId, {
          id: addonId,
          name: addonItem.name,
          nameAr: addonItem.nameAr,
          price: addonItem.price,
          order: addonItem.order
        });
      }

      if (!isErrorInfo(response)) {
        notify('update-addon-success', 'Add-on updated successfully', 'success');
        onPackageUpdate({
          ...pkg,
          addOns: (pkg.addOns || []).map((addon) => {
            const currentId = String(addon.id || addon._id || '');
            return currentId === addonId ? { ...addon, ...response } : addon;
          })
        });
      } else {
        notify('update-addon-failed', 'Failed to update add-on', 'danger');
      }
    } catch {
      notify('update-addon-error', 'An error occurred while updating add-on', 'danger');
    } finally {
      setSavingAddOns((prev) => {
        const next = new Set(prev);
        next.delete(addonId);
        return next;
      });
    }
  };

  const handleDeleteAddOnItem = (addOnGroupId: string, addonItemId?: string | number): void => {
    const addonId = String(addonItemId || '');
    if (!addonId) return;

    onConfirmDialog(
      'Delete Add-On',
      'Are you sure you want to delete this add-on? This action cannot be undone.',
      () => {
        deleteAddOn(addonId).then((response: any) => {
          if (!isErrorInfo(response)) {
            notify('delete-addon-success', 'Add-on deleted successfully', 'success');
            onPackageUpdate({
              ...pkg,
              addOns: (pkg.addOns || []).filter((addon) => {
                const currentId = String(addon.id || addon._id || '');
                return currentId !== addonId;
              })
            });
          } else {
            notify('delete-addon-failed', 'Failed to delete add-on', 'danger');
          }
        });
      }
    );
  };

  const handleAddOnItemChange = (
    addOnGroupId: string,
    addonIndex: number,
    field: 'name' | 'nameAr' | 'price' | 'order',
    value: string | number
  ): void => {
    const addonsInGroup = (pkg.addOns || []).filter((addon) => addon.unitTypeId === addOnGroupId);
    const targetAddon = addonsInGroup[addonIndex];

    if (!targetAddon) return;

    onPackageUpdate({
      ...pkg,
      addOns: (pkg.addOns || []).map((addon) =>
        (addon.id === targetAddon.id && addon.id) || (addon._id === targetAddon._id && addon._id)
          ? { ...addon, [field]: value }
          : addon
      )
    });
  };

  const handleAddAddOnItemToExistingGroup = (addOnGroupId: string): void => {
    const newAddOn: AddOn = {
      name: '',
      nameAr: '',
      price: 0,
      unitTypeId: addOnGroupId,
      packageId: pkg.id || '',
      id: generateId()
    };

    onPackageUpdate({
      ...pkg,
      addOns: [...(pkg.addOns || []), newAddOn]
    });
  };

  const groupedAddOns = React.useMemo(() => {
    if (!pkg.addOns || !Array.isArray(pkg.addOns)) return [];

    const groups = new Map<string, AddOn[]>();

    pkg.addOns.forEach((addon) => {
      const unitTypeId = addon.unitTypeId;
      if (!groups.has(unitTypeId)) {
        groups.set(unitTypeId, []);
      }
      groups.get(unitTypeId)?.push(addon);
    });

    return Array.from(groups.entries()).map(([unitTypeId, addons]) => ({
      id: unitTypeId,
      unitTypeId,
      addons
    }));
  }, [pkg.addOns]);

  // Filter out unit types that are already used in add-on groups
  const availableUnitTypeOptions = React.useMemo(() => {
    const usedUnitTypeIds = new Set(groupedAddOns.map((group) => group.unitTypeId));
    return unitTypeOptions.filter((option) => !usedUnitTypeIds.has(option.value));
  }, [unitTypeOptions, groupedAddOns]);

  return (
    <article className={styles['furnishing-management__card']}>
      <img src={pkg.image} alt={pkg.name} className={styles['furnishing-management__card-image']} />
      <div className={styles['furnishing-management__card-content']}>
        {isEditingPackage ? (
          <div className={styles['furnishing-management__package-edit-section']}>
            <div>
              <label
                className={styles['furnishing-management__input']}
                style={{ margin: 'auto', width: '150px' }}
              >
                <span>Package Order</span>
                <input
                  type="number"
                  placeholder="Package order"
                  value={pkg.order || ''}
                  onChange={(e) => handlePackageChange('order', e.target.value)}
                />
              </label>
            </div>
            <div className={styles['furnishing-management__form-grid']}>
              <label className={styles['furnishing-management__input']}>
                <span>Package Name (EN)</span>
                <input
                  type="text"
                  placeholder="Package name"
                  value={pkg.name}
                  onChange={(e) => handlePackageChange('name', e.target.value)}
                />
              </label>
              <label className={styles['furnishing-management__input']}>
                <span>Package Name (AR)</span>
                <input
                  type="text"
                  placeholder="اسم الحزمة"
                  value={pkg.nameAr}
                  dir="rtl"
                  onChange={(e) => handlePackageChange('nameAr', e.target.value)}
                />
              </label>
            </div>

            <div className={styles['furnishing-management__form-grid']}>
              <label className={styles['furnishing-management__input']}>
                <span>Description (EN)</span>
                <textarea
                  placeholder="Package description"
                  value={pkg.descriptionEn}
                  rows={3}
                  onChange={(e) => handlePackageChange('descriptionEn', e.target.value)}
                />
              </label>
              <label className={styles['furnishing-management__input']}>
                <span>Description (AR)</span>
                <textarea
                  placeholder="وصف الحزمة"
                  value={pkg.descriptionAr}
                  dir="rtl"
                  rows={3}
                  onChange={(e) => handlePackageChange('descriptionAr', e.target.value)}
                />
              </label>
            </div>

            <div className={styles['furnishing-management__input']}>
              <span>Package Image</span>
              <ImageUploader
                images={packageImages}
                onImageChange={handlePackageImageChange}
                existingImageUrl={pkg.image}
                maxNumber={1}
                altText="Package"
              />
            </div>

            <div className={styles['furnishing-management__package-edit-actions']}>
              <button
                type="button"
                onClick={handleToggleEditPackage}
                className={styles['furnishing-management__cancel-btn']}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePackageAndClose}
                className={styles['furnishing-management__save-btn']}
                disabled={savingPackage}
              >
                {savingPackage ? 'Saving...' : 'Save Package'}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles['furnishing-management__package-header']}>
            <div
              style={{
                textAlign: 'start',
                width: '80%'
              }}
            >
              <h3 dir={displayLanguage === 'ar' ? 'rtl' : 'ltr'}>
                {displayLanguage === 'en' ? pkg.name : pkg.nameAr}
              </h3>
              <p
                className={styles['furnishing-management__card-description']}
                dir={displayLanguage === 'ar' ? 'rtl' : 'ltr'}
              >
                {displayLanguage === 'en' ? pkg.descriptionEn : pkg.descriptionAr}
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleEditPackage}
              className={styles['furnishing-management__edit-btn']}
            >
              Edit Package
            </button>
          </div>
        )}

        <div className={styles['furnishing-management__card-models']}>
          <div className={styles['furnishing-management__card-models-header']}>
            <span>Styles ({pkg.models?.length || 0})</span>
            <button
              type="button"
              onClick={handleAddModel}
              className={styles['furnishing-management__add-btn']}
            >
              + Add Style
            </button>
          </div>

          {(pkg.models || []).map((model, modelIndex) => {
            const isExpanded = expandedModels.has(model.id);

            return (
              <div key={model.id} className={styles['furnishing-management__model']}>
                <div className={styles['furnishing-management__model-header']}>
                  <div className={styles['furnishing-management__model-info']}>
                    {model.image && (
                      <div className={styles['furnishing-management__model-thumbnail']}>
                        <img src={model.image} alt={model.name || 'Model'} />
                      </div>
                    )}
                    <div className={styles['furnishing-management__model-details']}>
                      <div className={styles['furnishing-management__model-title-row']}>
                        {model.name ? (
                          <>
                            <h4 className={styles['furnishing-management__model-name']}>
                              {displayLanguage === 'en' ? model.name : model.nameAr || model.name}
                            </h4>
                            {model.nameAr && displayLanguage === 'en' && (
                              <span
                                className={styles['furnishing-management__model-name-secondary']}
                              >
                                {model.nameAr}
                              </span>
                            )}
                            {model.name && displayLanguage === 'ar' && (
                              <span
                                className={styles['furnishing-management__model-name-secondary']}
                              >
                                {model.name}
                              </span>
                            )}
                            <div className={styles['furnishing-management__model-color-info']}>
                              <div
                                className={styles['furnishing-management__color-swatch']}
                                style={{ backgroundColor: model.color }}
                              />
                              <span className={styles['furnishing-management__color-label']}>
                                {model.color.toUpperCase()}
                              </span>
                            </div>
                          </>
                        ) : (
                          <h4 className={styles['furnishing-management__model-new-label']}>
                            Style {modelIndex + 1} (New)
                          </h4>
                        )}
                      </div>
                      <div className={styles['furnishing-management__model-stats']}>
                        <div className={styles['furnishing-management__model-stat']}>
                          <img
                            src={`${PUBLIC_PATH}/assets/svgs/items-icon.svg`}
                            alt="Show Rooms"
                            className={styles['furnishing-management__stat-icon']}
                            width="16"
                            height="16"
                          />
                          <span className={styles['furnishing-management__stat-value']}>
                            {model.showRooms?.length || 0}
                          </span>
                          <span className={styles['furnishing-management__stat-label']}>
                            Show Rooms
                          </span>
                        </div>
                        <div className={styles['furnishing-management__model-stat']}>
                          <img
                            src={`${PUBLIC_PATH}/assets/svgs/prices-icon.svg`}
                            alt="Prices"
                            className={styles['furnishing-management__stat-icon']}
                            width="16"
                            height="16"
                          />
                          <span className={styles['furnishing-management__stat-value']}>
                            {model.prices?.length || 0}
                          </span>
                          <span className={styles['furnishing-management__stat-label']}>
                            Prices
                          </span>
                        </div>
                        <div className={styles['furnishing-management__model-stat']}>
                          <img
                            src={`${PUBLIC_PATH}/assets/svgs/show-rooms-icon.svg`}
                            alt="Items"
                            className={styles['furnishing-management__stat-icon']}
                            width="16"
                            height="16"
                          />
                          <span className={styles['furnishing-management__stat-value']}>
                            {model.showRooms?.reduce(
                              (acc, sr) => acc + (sr.itemSpecs?.length || 0),
                              0
                            ) || 0}
                          </span>
                          <span className={styles['furnishing-management__stat-label']}>Items</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles['furnishing-management__model-actions']}>
                    <button
                      type="button"
                      onClick={() => handleToggleModel(model.id)}
                      className={styles['furnishing-management__toggle-btn']}
                    >
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveModel(model.id)}
                      className={styles['furnishing-management__remove-btn']}
                      disabled={isTemporaryId(model.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className={styles['furnishing-management__form-grid']}>
                      <label className={styles['furnishing-management__input']}>
                        <span>Style Order</span>
                        <input
                          type="number"
                          placeholder="Style order"
                          value={model.order || ''}
                          onChange={(e) => handleModelChange(model.id, 'order', e.target.value)}
                        />
                      </label>
                    </div>
                    <div className={styles['furnishing-management__form-grid']}>
                      <div>
                        <label className={styles['furnishing-management__input']}>
                          <span>Color</span>
                          <div className={styles['furnishing-management__color-picker-container']}>
                            <div
                              className={styles['furnishing-management__color-swatch-wrapper']}
                              style={{ backgroundColor: model.color || '#ffffff' }}
                              title="Pick a color"
                            >
                              <input
                                type="color"
                                value={model.color || '#ffffff'}
                                onChange={(e) =>
                                  handleModelChange(model.id, 'color', e.target.value)
                                }
                                className={styles['furnishing-management__color-input-hidden']}
                              />
                            </div>
                            <input
                              type="text"
                              value={model.color}
                              onChange={(e) => handleModelChange(model.id, 'color', e.target.value)}
                              placeholder="#000000"
                              className={styles['furnishing-management__color-text-input']}
                            />
                          </div>
                        </label>
                        <label className={styles['furnishing-management__input']}>
                          <span>Style Name (EN)</span>
                          <input
                            type="text"
                            placeholder="type name ex: saga"
                            value={model.name}
                            onChange={(e) => handleModelChange(model.id, 'name', e.target.value)}
                          />
                        </label>
                        <label className={styles['furnishing-management__input']}>
                          <span>Style Name (AR)</span>
                          <input
                            type="text"
                            placeholder="اسم الستايل مثل: ساجا"
                            value={model.nameAr}
                            dir="rtl"
                            onChange={(e) => handleModelChange(model.id, 'nameAr', e.target.value)}
                          />
                        </label>
                      </div>
                      <div className={styles['furnishing-management__input']}>
                        <span>Style Image</span>
                        <span className={styles['furnishing-management__helper-text']}>
                          Upload a square image (584 x 584 px) for the sharpest display.
                        </span>

                        <ImageUploader
                          images={modelImages[model.id] || []}
                          onImageChange={(imageList) => handleModelImageChange(model.id, imageList)}
                          existingImageUrl={model.image}
                          maxNumber={1}
                          altText="Style"
                        />
                      </div>
                    </div>
                    <div className={styles['furnishing-management__model-save-section']}>
                      <button
                        type="button"
                        onClick={() => handleSaveModel(model)}
                        className={styles['furnishing-management__save-btn']}
                        disabled={!model.name || !model.nameAr || savingModels.has(model.id)}
                      >
                        {savingModels.has(model.id) ? 'Saving...' : 'Save Style'}
                      </button>
                    </div>
                    <div className={styles['furnishing-management__pricing-section']}>
                      {(model?.prices || []).length > 0 && (
                        <div className={styles['furnishing-management__pricing-toggle']}>
                          <button
                            type="button"
                            onClick={() => handleTogglePrices(model.id)}
                            className={styles['furnishing-management__toggle-sm-btn']}
                          >
                            {collapsedPrices.has(model.id) ? 'Show Prices' : 'Hide Prices'}
                          </button>
                        </div>
                      )}
                      <SectionHeader
                        title="Pricing by Bedrooms"
                        buttonText="+ Add Price"
                        onButtonClick={() => handleAddPrice(model.id)}
                        disabled={isTemporaryId(model.id)}
                      />

                      {(model.prices || []).length > 0 && !collapsedPrices.has(model.id) && (
                        <div className={styles['furnishing-management__prices-list']}>
                          {(model.prices || []).map((price, priceIndex) => (
                            <div
                              key={price.id || priceIndex}
                              className={styles['furnishing-management__price-item']}
                            >
                              <div
                                className={`${styles['furnishing-management__input']} ${styles['furnishing-management__select']}`}
                              >
                                <span>Number of Rooms</span>
                                <Select
                                  options={unitTypeOptions}
                                  value={unitTypeOptions.find(
                                    (opt) => opt.value === price.unitTypeGroupId
                                  )}
                                  onChange={(
                                    selected: ValueType<{ value: string; label: string }, false>
                                  ) => {
                                    if (selected) {
                                      handlePriceChange(
                                        model.id,
                                        priceIndex,
                                        'unitTypeGroupId',
                                        (selected as { value: string; label: string }).value
                                      );
                                    }
                                  }}
                                  placeholder="Select Number of Rooms"
                                  isLoading={loadingUnitTypes}
                                  isClearable={false}
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <label className={styles['furnishing-management__input']}>
                                <span>Price</span>
                                <input
                                  type="text"
                                  placeholder="Price"
                                  value={price.price}
                                  onChange={(e) =>
                                    handlePriceChange(
                                      model.id,
                                      priceIndex,
                                      'price',
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                              </label>
                              <div style={{ textAlign: 'right' }}>
                                <button
                                  type="button"
                                  onClick={() => handleRemovePrice(model.id, priceIndex)}
                                  className={styles['furnishing-management__remove-btn']}
                                  style={{ width: '100px', margin: 'auto' }}
                                  title="Remove price"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(model.prices || []).length > 0 && !collapsedPrices.has(model.id) && (
                        <div style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            onClick={() => handleSavePrices(model.id, model.prices || [])}
                            className={styles['furnishing-management__save-btn']}
                            disabled={isTemporaryId(model.id) || savingPrices.has(model.id)}
                          >
                            {savingPrices.has(model.id) ? 'Saving Prices...' : 'Save Prices'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className={styles['furnishing-management__showrooms']}>
                      <SectionHeader
                        title="Show Rooms"
                        buttonText="+ Add Show Room"
                        onButtonClick={() => handleAddShowRoom(model.id)}
                        disabled={isTemporaryId(model.id)}
                      />

                      {model.showRooms.map((showRoom, srIndex) => {
                        const isShowRoomExpanded = expandedShowRooms.has(showRoom.id);

                        return (
                          <div
                            key={showRoom.id}
                            className={styles['furnishing-management__showroom']}
                          >
                            <div className={styles['furnishing-management__showroom-header']}>
                              <div className={styles['furnishing-management__showroom-title']}>
                                {showRoom.name ? (
                                  <span className={styles['furnishing-management__showroom-name']}>
                                    {displayLanguage === 'en'
                                      ? showRoom.name
                                      : showRoom.nameAr || showRoom.name}
                                  </span>
                                ) : (
                                  <span>Show Room {srIndex + 1}</span>
                                )}
                              </div>

                              <div className={styles['furnishing-management__showroom-actions']}>
                                <button
                                  type="button"
                                  onClick={() => handleToggleShowRoom(showRoom.id)}
                                  className={styles['furnishing-management__toggle-sm-btn']}
                                >
                                  {isShowRoomExpanded ? 'Collapse' : 'Expand'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveShowRoom(model.id, showRoom.id)}
                                  className={styles['furnishing-management__remove-sm-btn']}
                                  disabled={isTemporaryId(showRoom.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>

                            {isShowRoomExpanded && (
                              <>
                                <div className={styles['furnishing-management__form-grid']}>
                                  <label className={styles['furnishing-management__input']}>
                                    <span>Show Room Order</span>
                                    <input
                                      type="number"
                                      placeholder="Show room order"
                                      value={showRoom.order || ''}
                                      onChange={(e) =>
                                        handleShowRoomChange(
                                          model.id,
                                          showRoom.id,
                                          'order',
                                          e.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className={styles['furnishing-management__input']}>
                                    <span>Room Name (EN)</span>
                                    <input
                                      type="text"
                                      placeholder="Living room"
                                      value={showRoom.name}
                                      onChange={(e) =>
                                        handleShowRoomChange(
                                          model.id,
                                          showRoom.id,
                                          'name',
                                          e.target.value
                                        )
                                      }
                                    />
                                  </label>
                                  <label className={styles['furnishing-management__input']}>
                                    <span>Room Name (AR)</span>
                                    <input
                                      type="text"
                                      placeholder="غرفة المعيشة"
                                      value={showRoom.nameAr}
                                      dir="rtl"
                                      onChange={(e) =>
                                        handleShowRoomChange(
                                          model.id,
                                          showRoom.id,
                                          'nameAr',
                                          e.target.value
                                        )
                                      }
                                    />
                                  </label>
                                </div>

                                <div className={styles['furnishing-management__input']}>
                                  <span>Show Room Images (multiple)</span>
                                  <span className={styles['furnishing-management__helper-text']}>
                                    Upload a image (576 px x 576 px) for the sharpest display.
                                  </span>
                                  <ImageUploader
                                    images={
                                      showRoomImages[showRoom.id] ||
                                      showRoom.images.map((img: string) => ({
                                        dataURL: img
                                      }))
                                    }
                                    onImageChange={(imageList) =>
                                      handleShowRoomImageChange(model.id, showRoom.id, imageList)
                                    }
                                    multiple
                                    maxNumber={10}
                                    minImages={1}
                                    altText="ShowRoom view"
                                  />
                                  {!isTemporaryId(showRoom.id) &&
                                    !savingShowRoomImages.has(showRoom.id) && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleUpdateShowRoomImages(model.id, showRoom.id)
                                        }
                                        className={styles['furnishing-management__save-btn']}
                                        disabled={
                                          isTemporaryId(showRoom.id) ||
                                          savingShowRoomImages.has(showRoom.id)
                                        }
                                        style={{ marginTop: '8px' }}
                                      >
                                        {savingShowRoomImages.has(showRoom.id)
                                          ? 'Updating Images...'
                                          : 'Update Images'}
                                      </button>
                                    )}
                                </div>

                                <div
                                  className={styles['furnishing-management__showroom-save-section']}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleSaveShowRoom(model.id, showRoom)}
                                    className={styles['furnishing-management__save-btn']}
                                    disabled={
                                      !showRoom.name ||
                                      !showRoom.nameAr ||
                                      savingShowRooms.has(showRoom.id)
                                    }
                                  >
                                    {savingShowRooms.has(showRoom.id)
                                      ? 'Saving...'
                                      : 'Save Show Room'}
                                  </button>
                                </div>

                                <div className={styles['furnishing-management__itemspecs']}>
                                  <SectionHeader
                                    title="Item Specifications"
                                    buttonText="+ Add Item"
                                    onButtonClick={() => handleAddItemSpec(model.id, showRoom.id)}
                                    disabled={isTemporaryId(showRoom.id)}
                                  />

                                  {showRoom.itemSpecs.map((itemSpec, isIndex) => {
                                    const isItemSpecExpanded = expandedItemSpecs.has(itemSpec.id);

                                    return (
                                      <div
                                        key={itemSpec.id}
                                        className={styles['furnishing-management__itemspec']}
                                      >
                                        <div
                                          className={
                                            styles['furnishing-management__itemspec-header']
                                          }
                                        >
                                          <div
                                            className={
                                              styles['furnishing-management__itemspec-title']
                                            }
                                          >
                                            {itemSpec?.name ? (
                                              <span
                                                className={
                                                  styles['furnishing-management__itemspec-name']
                                                }
                                              >
                                                {displayLanguage === 'en'
                                                  ? itemSpec.name
                                                  : itemSpec.nameAr || itemSpec.name}
                                              </span>
                                            ) : (
                                              <span>Item {isIndex + 1}</span>
                                            )}
                                          </div>

                                          <div
                                            className={
                                              styles['furnishing-management__itemspec-actions']
                                            }
                                          >
                                            <button
                                              type="button"
                                              onClick={() => handleToggleItemSpec(itemSpec.id)}
                                              className={
                                                styles['furnishing-management__toggle-sm-btn']
                                              }
                                            >
                                              {isItemSpecExpanded ? 'Collapse' : 'Expand'}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveItemSpec(
                                                  model.id,
                                                  showRoom.id,
                                                  itemSpec.id
                                                )
                                              }
                                              className={
                                                styles['furnishing-management__remove-sm-btn']
                                              }
                                              disabled={isTemporaryId(itemSpec.id)}
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        </div>

                                        {isItemSpecExpanded && (
                                          <>
                                            <div
                                              className={styles['furnishing-management__form-grid']}
                                            >
                                              <label
                                                className={styles['furnishing-management__input']}
                                              >
                                                <span>Item Order</span>
                                                <input
                                                  type="number"
                                                  placeholder="Item order"
                                                  value={itemSpec.order || ''}
                                                  onChange={(e) =>
                                                    handleItemSpecChange(
                                                      model.id,
                                                      showRoom.id,
                                                      itemSpec.id,
                                                      'order',
                                                      parseInt(e.target.value, 10) || 0
                                                    )
                                                  }
                                                />
                                              </label>
                                            </div>
                                            <div
                                              className={styles['furnishing-management__form-grid']}
                                            >
                                              <label
                                                className={styles['furnishing-management__input']}
                                              >
                                                <span>Item Name (EN)</span>
                                                <input
                                                  type="text"
                                                  placeholder="Bed"
                                                  value={itemSpec.name}
                                                  onChange={(e) =>
                                                    handleItemSpecChange(
                                                      model.id,
                                                      showRoom.id,
                                                      itemSpec.id,
                                                      'name',
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </label>
                                              <label
                                                className={styles['furnishing-management__input']}
                                              >
                                                <span>Item Name (AR)</span>
                                                <input
                                                  type="text"
                                                  placeholder="سرير"
                                                  value={itemSpec.nameAr}
                                                  dir="rtl"
                                                  onChange={(e) =>
                                                    handleItemSpecChange(
                                                      model.id,
                                                      showRoom.id,
                                                      itemSpec.id,
                                                      'nameAr',
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </label>
                                            </div>

                                            <div
                                              className={styles['furnishing-management__form-grid']}
                                            >
                                              <div
                                                className={styles['furnishing-management__input']}
                                              >
                                                <span>Description (EN)</span>
                                                <CKEditor
                                                  editor={ClassicEditor}
                                                  data={itemSpec.description}
                                                  onChange={(
                                                    _event: unknown,
                                                    editor: { getData: () => string }
                                                  ) => {
                                                    const newData: string = editor.getData();
                                                    handleItemSpecChange(
                                                      model.id,
                                                      showRoom.id,
                                                      itemSpec.id,
                                                      'description',
                                                      newData
                                                    );
                                                  }}
                                                />
                                              </div>
                                              <div
                                                className={styles['furnishing-management__input']}
                                              >
                                                <span>Description (AR)</span>
                                                <CKEditor
                                                  editor={ClassicEditor}
                                                  data={itemSpec.descriptionAr}
                                                  onChange={(
                                                    _event: unknown,
                                                    editor: { getData: () => string }
                                                  ) => {
                                                    const newData: string = editor.getData();
                                                    handleItemSpecChange(
                                                      model.id,
                                                      showRoom.id,
                                                      itemSpec.id,
                                                      'descriptionAr',
                                                      newData
                                                    );
                                                  }}
                                                />
                                              </div>
                                            </div>

                                            <div className={styles['furnishing-management__input']}>
                                              <span>Item Image</span>
                                              <span
                                                className={
                                                  styles['furnishing-management__helper-text']
                                                }
                                              >
                                                Upload a image (300 x 345 px) for the sharpest
                                                display.
                                              </span>
                                              <ImageUploader
                                                images={itemSpecImages[itemSpec.id] || []}
                                                onImageChange={(imageList) =>
                                                  handleItemSpecImageChange(
                                                    model.id,
                                                    showRoom.id,
                                                    itemSpec.id,
                                                    imageList
                                                  )
                                                }
                                                existingImageUrl={itemSpec.image}
                                                maxNumber={1}
                                                altText="Item"
                                              />
                                            </div>

                                            <div
                                              className={
                                                styles[
                                                  'furnishing-management__itemspec-save-section'
                                                ]
                                              }
                                            >
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleSaveItemSpec(
                                                    model.id,
                                                    showRoom.id,
                                                    itemSpec
                                                  )
                                                }
                                                className={
                                                  styles['furnishing-management__save-btn']
                                                }
                                                disabled={
                                                  !itemSpec.name ||
                                                  !itemSpec.nameAr ||
                                                  savingItemSpecs.has(itemSpec.id)
                                                }
                                              >
                                                {savingItemSpecs.has(itemSpec.id)
                                                  ? 'Saving...'
                                                  : 'Save Item'}
                                              </button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* AddOns Section */}
        <div className={styles['furnishing-management__addons-section']}>
          <SectionHeader
            title="Add-Ons"
            buttonText="+ Add Add-On Group"
            onButtonClick={handleStartNewAddOnGroup}
            disabled={!pkg.id || isTemporaryId(pkg.id ?? '') || !!newAddOnGroup}
          />

          {/* New Add-On Group Form */}
          {newAddOnGroup && (
            <div className={styles['furnishing-management__addon-group-new']}>
              <div
                className={styles['furnishing-management__addon-group-header']}
                style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem' }}
              >
                <h4 style={{ fontSize: '1.4rem', color: '#237c58' }}>New Add-On Group</h4>
              </div>

              <div
                className={`${styles['furnishing-management__input']} ${styles['furnishing-management__select']}`}
                style={{ marginBottom: '1.5rem' }}
              >
                <span style={{ fontSize: '1rem', color: '#555' }}>Number of Bedrooms</span>
                <Select
                  options={availableUnitTypeOptions}
                  value={availableUnitTypeOptions.find(
                    (opt) => opt.value === newAddOnGroup.unitTypeId
                  )}
                  onChange={(selected: ValueType<{ value: string; label: string }, false>) => {
                    if (selected) {
                      handleNewAddOnGroupUnitTypeChange(
                        (selected as { value: string; label: string }).value
                      );
                    }
                  }}
                  placeholder="Select Number of Bedrooms"
                  isLoading={loadingUnitTypes}
                  isClearable={false}
                  classNamePrefix="react-select"
                />
              </div>

              <div className={styles['furnishing-management__addon-items']}>
                <div className={styles['furnishing-management__addon-items-header']}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>Add-On Items</span>
                  <button
                    type="button"
                    onClick={handleAddNewAddOnItem}
                    className={styles['furnishing-management__add-sm-btn']}
                  >
                    + Add AddOns
                  </button>
                </div>

                {newAddOnGroup.addons.map((addon, index) => {
                  const selectedOption = addOnNameOptions.find((opt) => opt.value === addon.name);
                  return (
                    <div
                      key={index}
                      className={styles['furnishing-management__addon-item']}
                      style={{ alignItems: 'center' }}
                    >
                      <div className={styles['furnishing-management__select']}>
                        <Select
                          options={addOnNameOptions}
                          value={selectedOption}
                          onChange={(
                            selected: ValueType<
                              { value: string; label: string; labelAr: string },
                              false
                            >
                          ) => {
                            if (selected && newAddOnGroup) {
                              const option = selected as {
                                value: string;
                                label: string;
                                labelAr: string;
                              };
                              const updatedAddons = [...newAddOnGroup.addons];
                              updatedAddons[index] = {
                                ...updatedAddons[index],
                                name: option.value,
                                nameAr: option.labelAr
                              };
                              setNewAddOnGroup({ ...newAddOnGroup, addons: updatedAddons });
                            }
                          }}
                          placeholder="Add-On"
                          isClearable={false}
                          classNamePrefix="react-select"
                        />
                      </div>
                      <div className={styles['furnishing-management__input']}>
                        <input
                          type="number"
                          placeholder="Price"
                          value={addon.price}
                          onChange={(e) =>
                            handleNewAddOnItemChange(
                              index,
                              'price',
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />
                      </div>
                      <div className={styles['furnishing-management__input']}>
                        <input
                          type="number"
                          placeholder="Order"
                          value={addon.order || ''}
                          onChange={(e) =>
                            handleNewAddOnItemChange(
                              index,
                              'order',
                              parseInt(e.target.value, 10) || 0
                            )
                          }
                          style={{ width: '80px' }}
                        />
                      </div>
                      <div className={styles['furnishing-management__addon-item-actions']}>
                        {newAddOnGroup.addons.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveNewAddOnItem(index)}
                            className={styles['furnishing-management__remove-sm-btn']}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={styles['furnishing-management__addon-group-actions']}>
                <button
                  type="button"
                  onClick={handleCancelNewAddOnGroup}
                  className={styles['furnishing-management__cancel-btn']}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveNewAddOnGroup}
                  className={styles['furnishing-management__save-btn']}
                  disabled={savingAddOns.has('new')}
                >
                  {savingAddOns.has('new') ? 'Saving...' : 'Save Add-On Group'}
                </button>
              </div>
            </div>
          )}

          {/* Existing Add-On Groups */}
          {groupedAddOns.map((addOnGroup) => {
            const isExpanded = expandedAddOns.has(addOnGroup.id ?? '');
            const unitTypeLabel =
              unitTypeOptions.find((opt) => opt.value === addOnGroup.unitTypeId)?.label ||
              addOnGroup.unitTypeId;

            return (
              <div key={addOnGroup.id} className={styles['furnishing-management__addon-group']}>
                <div className={styles['furnishing-management__addon-group-header']}>
                  <div className={styles['furnishing-management__addon-group-info']}>
                    <h4 style={{ fontSize: '1.2rem', color: '#333' }}>
                      {unitTypeLabel}{' '}
                      <span style={{ color: '#888', fontWeight: 400, fontSize: '1rem' }}>
                        ({addOnGroup.addons?.length || 0} addon
                        {(addOnGroup.addons?.length || 0) !== 1 ? 's' : ''})
                      </span>
                    </h4>
                  </div>
                  <div
                    className={styles['furnishing-management__addon-group-actions']}
                    style={{ marginTop: 0 }}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleAddOnGroup(addOnGroup.id ?? '')}
                      className={styles['furnishing-management__toggle-sm-btn']}
                    >
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <>
                    <div className={styles['furnishing-management__addon-items']}>
                      <div className={styles['furnishing-management__addon-items-header']}>
                        <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>Add-On Items</span>
                        <button
                          type="button"
                          onClick={() => handleAddAddOnItemToExistingGroup(addOnGroup.id ?? '')}
                          className={styles['furnishing-management__add-sm-btn']}
                        >
                          + Add AddOns
                        </button>
                      </div>

                      {!addOnGroup.addons || addOnGroup.addons.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                          No add-on items. Click "+ Add Item" to add one.
                        </div>
                      ) : (
                        addOnGroup.addons.map((addon, index) => {
                          const selectedOption = addOnNameOptions.find(
                            (opt) => opt.value === addon.name
                          );

                          return (
                            <div
                              key={addon.id || index}
                              className={styles['furnishing-management__addon-item']}
                              style={{ alignItems: 'center' }}
                            >
                              <div className={styles['furnishing-management__select']}>
                                <Select
                                  options={addOnNameOptions}
                                  value={selectedOption}
                                  onChange={(
                                    selected: ValueType<
                                      { value: string; label: string; labelAr: string },
                                      false
                                    >
                                  ) => {
                                    if (selected) {
                                      const option = selected as {
                                        value: string;
                                        label: string;
                                        labelAr: string;
                                      };
                                      const addonsInGroup = (pkg.addOns || []).filter(
                                        (addon) => addon.unitTypeId === addOnGroup.id
                                      );
                                      const targetAddon = addonsInGroup[index];

                                      if (!targetAddon) return;

                                      onPackageUpdate({
                                        ...pkg,
                                        addOns: (pkg.addOns || []).map((addon) =>
                                          (addon.id === targetAddon.id && addon.id) ||
                                          (addon._id === targetAddon._id && addon._id)
                                            ? {
                                                ...addon,
                                                name: option.value,
                                                nameAr: option.labelAr
                                              }
                                            : addon
                                        )
                                      });
                                    }
                                  }}
                                  placeholder="Add-On"
                                  isClearable={false}
                                  classNamePrefix="react-select"
                                  menuPortalTarget={document.body}
                                />
                              </div>
                              <div className={styles['furnishing-management__input']}>
                                <input
                                  type="number"
                                  placeholder="Price"
                                  value={addon.price || ''}
                                  style={{ width: '100px' }}
                                  onChange={(e) =>
                                    handleAddOnItemChange(
                                      addOnGroup.id ?? '',
                                      index,
                                      'price',
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                />
                              </div>
                              <div className={styles['furnishing-management__input']}>
                                <input
                                  type="number"
                                  placeholder="Order"
                                  value={addon.order || ''}
                                  style={{ width: '80px' }}
                                  onChange={(e) =>
                                    handleAddOnItemChange(
                                      addOnGroup.id ?? '',
                                      index,
                                      'order',
                                      parseInt(e.target.value, 10) || 0
                                    )
                                  }
                                />
                              </div>
                              <div className={styles['furnishing-management__addon-item-actions']}>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateAddOnItem(addon)}
                                  className={styles['furnishing-management__save-sm-btn']}
                                  disabled={
                                    savingAddOns.has(String(addon.id || addon._id || '')) ||
                                    !addon.id ||
                                    !addon.name ||
                                    !addon.nameAr ||
                                    !addon.price
                                  }
                                >
                                  {savingAddOns.has(String(addon.id || addon._id || ''))
                                    ? 'Saving...'
                                    : 'Save'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDeleteAddOnItem(
                                      addOnGroup.id ?? '',
                                      addon.id || addon._id
                                    )
                                  }
                                  className={styles['furnishing-management__remove-sm-btn']}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '4px',
                                    minWidth: '32px'
                                  }}
                                  title="Remove add-on"
                                >
                                  <img
                                    src={`${PUBLIC_PATH}/assets/svgs/close.svg`}
                                    alt="Delete"
                                    width="12"
                                    height="12"
                                  />
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className={styles['furnishing-management__card-button']}
          onClick={handleRemovePackage}
        >
          <span style={{ position: 'relative', zIndex: 1 }}>Remove Package</span>
        </button>
      </div>
    </article>
  );
};
